import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { isStaff } from "@/lib/staffAccess";

const FALLBACK_LAUNCH = "2026-10-24T16:00:00-04:00";
const LAUNCHED_KEY = "abfa-site-launched";
// Staff entry points that must work before launch (admin pages themselves require login)
const OPEN_PATHS = [/^\/login/, /^\/auth/, /^\/admin/, /^\/big-screen/, /\/big-screen$/, /\/stadium$/];

const pad = (n) => String(n).padStart(2, "0");

const useLaunchClock = () => {
  const [state, setState] = useState({ ready: false, launchMs: new Date(FALLBACK_LAUNCH).getTime(), skew: 0 });
  useEffect(() => {
    if (localStorage.getItem(LAUNCHED_KEY)) { setState((s) => ({ ...s, ready: true, launchMs: 0 })); return; }
    const t0 = Date.now();
    axios.get(`${API}/site/launch`, { timeout: 8000 })
      .then(({ data }) => {
        const rtt = Date.now() - t0;
        const skew = new Date(data.server_time).getTime() + rtt / 2 - Date.now();
        setState({ ready: true, launchMs: new Date(data.launch_at).getTime(), skew });
      })
      .catch(() => setState((s) => ({ ...s, ready: true }))); // fall back to device clock
  }, []);
  return state;
};

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <div className="w-[72px] sm:w-[110px] md:w-[150px] py-3 md:py-5 bg-white/[0.05] border border-white/10 backdrop-blur-sm">
      <span className="block text-center font-heading font-black text-4xl sm:text-6xl md:text-7xl text-white tabular-nums leading-none">{pad(value)}</span>
    </div>
    <span className="mt-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-[#FFC72C]">{label}</span>
  </div>
);

const ComingSoon = ({ remaining }) => {
  const s = Math.max(0, Math.floor(remaining / 1000));
  return (
    <div className="min-h-screen bg-[#05080f] text-white relative overflow-hidden flex flex-col" data-testid="coming-soon-page">
      <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_15%_0%,rgba(0,158,96,0.22),transparent_60%),radial-gradient(800px_600px_at_100%_100%,rgba(255,199,44,0.14),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.04] bg-[repeating-linear-gradient(90deg,#fff_0_1px,transparent_1px_120px)]" />
      <main className="relative flex-1 flex flex-col items-center justify-center text-center px-5 py-14">
        <div className="w-32 h-28 md:w-44 md:h-36 bg-white p-2 shadow-2xl cs-rise" style={{ animationDelay: "0ms" }}>
          <img src="/affiliates/abfa.png" alt="Antigua & Barbuda Football Association crest" className="w-full h-full object-contain" />
        </div>
        <p className="mt-8 text-[11px] md:text-sm font-bold uppercase tracking-[0.4em] text-[#009E60] cs-rise" style={{ animationDelay: "120ms" }}>ABFA Football Centre</p>
        <h1 className="mt-4 font-heading font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-7xl max-w-4xl cs-rise" style={{ animationDelay: "220ms" }} data-testid="coming-soon-headline">
          A New Era of <span className="text-[#FFC72C]">Antigua &amp; Barbuda</span> Football
        </h1>
        <p className="mt-6 text-sm md:text-lg text-gray-300 max-w-2xl cs-rise" style={{ animationDelay: "320ms" }}>
          The ABFA Football Centre is almost here. Your new home for fixtures, results, live scores, standings, clubs, players and football news.
        </p>
        <div className="mt-10 flex gap-2 sm:gap-4 md:gap-6 cs-rise" style={{ animationDelay: "420ms" }} data-testid="countdown" aria-live="polite">
          <Unit value={Math.floor(s / 86400)} label="Days" />
          <Unit value={Math.floor((s % 86400) / 3600)} label="Hours" />
          <Unit value={Math.floor((s % 3600) / 60)} label="Minutes" />
          <Unit value={s % 60} label="Seconds" />
        </div>
        <p className="mt-8 text-xs md:text-sm text-gray-400 uppercase tracking-widest cs-rise" style={{ animationDelay: "520ms" }} data-testid="launch-date-text">
          Launching Saturday 24 October 2026 · 4:00 PM Antigua time
        </p>
      </main>
      <footer className="relative pb-6 flex flex-col items-center gap-2 text-[11px] text-gray-500">
        <p>© Antigua &amp; Barbuda Football Association · <a href="mailto:gensec@footballanu.org" className="hover:text-white">gensec@footballanu.org</a></p>
      </footer>
    </div>
  );
};

// Temporary public launch gate: Coming Soon until launch (server-time based), admins bypass, auto-reveal at launch.
export const LaunchGate = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const { ready, launchMs, skew } = useLaunchClock();
  const [now, setNow] = useState(Date.now());
  const done = useRef(false);

  const remaining = launchMs - (now + skew);
  const launched = ready && remaining <= 0;

  useEffect(() => {
    if (launched) {
      if (!done.current) { localStorage.setItem(LAUNCHED_KEY, "1"); done.current = true; }
      return undefined;
    }
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [launched]);

  if (launched || OPEN_PATHS.some((r) => r.test(pathname))) return children;
  if (!ready || loading) return <div className="min-h-screen bg-[#05080f]" data-testid="launch-gate-loading" />;
  if (isStaff(user)) return children;
  return <ComingSoon remaining={remaining} />;
};
